import { useMemo, useState } from 'react';
import { NewsCategory } from '@/types';
import { useCreateNewsCategory, useDeleteNewsCategory, useNewsCategoryList, useUpdateNewsCategory } from '@/hooks/useNewsCategory';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { BulkSelectionToolbar } from '@/components/list/BulkSelectionToolbar';
import { useBulkSelection } from '@/hooks/useBulkSelection';
import { Edit, Plus, Trash2 } from 'lucide-react';

const emptyForm: Partial<NewsCategory> = {
  name: '',
  sortOrder: 0,
  isActive: true,
};

export function NewsCategoryList() {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<number[]>([]);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<Partial<NewsCategory>>(emptyForm);

  const { data, isLoading } = useNewsCategoryList({ page, limit: 20 });
  const createCategory = useCreateNewsCategory();
  const updateCategory = useUpdateNewsCategory();
  const deleteCategory = useDeleteNewsCategory();
  const selection = useBulkSelection((data?.data || []).map((item) => item.id));

  const totalPage = useMemo(() => Math.max(1, Math.ceil((data?.total || 0) / 20)), [data?.total]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (item: NewsCategory) => {
    setEditingId(item.id);
    setForm({
      name: item.name,
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive,
    });
    setOpen(true);
  };

  const submitForm = async () => {
    if (!form.name?.trim()) return;

    const payload = {
      name: form.name.trim(),
      sortOrder: Number(form.sortOrder || 0),
      isActive: !!form.isActive,
    };

    if (editingId) {
      await updateCategory.mutateAsync({ id: editingId, data: payload });
    } else {
      await createCategory.mutateAsync(payload);
    }

    setOpen(false);
  };

  const submitDelete = async () => {
    if (!pendingDeleteIds.length) return;
    for (const id of pendingDeleteIds) {
      await deleteCategory.mutateAsync(id);
    }
    selection.clearSelection();
    setPendingDeleteIds([]);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight">Kategori Berita</h2>
          <p className="text-muted-foreground">Atur daftar kategori yang bisa dipilih pada form berita.</p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Tambah Kategori
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Daftar Kategori</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <BulkSelectionToolbar
            className="m-4 mb-0"
            selectedCount={selection.selectedCount}
            itemLabel="kategori"
            onClear={selection.clearSelection}
            onDelete={() => setPendingDeleteIds(selection.selectedIds)}
            deleteDisabled={deleteCategory.isPending}
          />
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox checked={selection.allSelected} onCheckedChange={(checked) => selection.toggleAll(Boolean(checked))} />
                </TableHead>
                <TableHead>Nama</TableHead>
                <TableHead>Urutan</TableHead>
                <TableHead>Aktif</TableHead>
                <TableHead className="w-28">Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow><TableCell colSpan={5} className="py-8 text-center">Memuat...</TableCell></TableRow>
              ) : data?.data.length ? (
                data.data.map((item) => (
                  <TableRow key={item.id} className={selection.isSelected(item.id) ? 'bg-muted/30' : undefined}>
                    <TableCell>
                      <Checkbox checked={selection.isSelected(item.id)} onCheckedChange={(checked) => selection.toggleOne(item.id, Boolean(checked))} />
                    </TableCell>
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>{item.sortOrder || 0}</TableCell>
                    <TableCell>{item.isActive ? 'Ya' : 'Tidak'}</TableCell>
                    <TableCell className="space-x-2">
                      <Button variant="outline" size="icon" onClick={() => openEdit(item)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="destructive" size="icon" onClick={() => setPendingDeleteIds([item.id])}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow><TableCell colSpan={5} className="py-8 text-center">Belum ada kategori berita.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <div className="flex items-center justify-end gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Sebelumnya</Button>
        <span className="text-sm text-muted-foreground">Halaman {page} dari {totalPage}</span>
        <Button variant="outline" size="sm" disabled={page >= totalPage} onClick={() => setPage((p) => p + 1)}>Selanjutnya</Button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? 'Ubah Kategori Berita' : 'Tambah Kategori Berita'}</DialogTitle>
            <DialogDescription>Nama kategori ini akan tersedia di form berita.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              placeholder="Nama kategori"
              value={form.name || ''}
              onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
            />
            <Input
              placeholder="Urutan"
              type="number"
              value={form.sortOrder ?? 0}
              onChange={(e) => setForm((v) => ({ ...v, sortOrder: Number(e.target.value) }))}
            />
            <div className="flex items-center gap-2">
              <Checkbox
                id="news-category-active"
                checked={!!form.isActive}
                onCheckedChange={(checked) => setForm((v) => ({ ...v, isActive: !!checked }))}
              />
              <label htmlFor="news-category-active" className="text-sm">Aktif</label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submitForm}>Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingDeleteIds.length > 0} onOpenChange={() => setPendingDeleteIds([])}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hapus Kategori Berita</DialogTitle>
            <DialogDescription>Tindakan ini tidak dapat dibatalkan.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPendingDeleteIds([])}>Batal</Button>
            <Button variant="destructive" onClick={submitDelete}>Hapus</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
